<template>
    <div
        v-if="timeLeft > 0"
        class="delivery-timeframe"
    >
        <LazyLoad when-idle>
            <i18n
                keypath="deliveryDays.timeframe.title"
                tag="p"
            >
                <template #date>
                    {{ formatDate(date) }}
                </template>

                <template #validUntil>
                    <template v-if="timeLeftFormatted.days">
                        {{
                            t(
                                'deliveryDays.timeframe.days',
                                timeLeftFormatted.days
                            )
                        }}
                    </template>

                    <template v-if="timeLeftFormatted.hours">
                        {{
                            ' ' +
                            t(
                                'deliveryDays.timeframe.hours',
                                timeLeftFormatted.hours
                            )
                        }}
                    </template>

                    <template v-if="timeLeftFormatted.minutes">
                        {{
                            ' ' +
                            t(
                                'deliveryDays.timeframe.minutes',
                                timeLeftFormatted.minutes
                            )
                        }}
                    </template>

                    <template v-if="timeLeftFormatted.seconds">
                        {{
                            ' ' +
                            t(
                                'deliveryDays.timeframe.seconds',
                                timeLeftFormatted.seconds
                            )
                        }}
                    </template>
                </template>
            </i18n>
        </LazyLoad>

        <slot name="default"></slot>
    </div>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue'
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DeliveryTimeframeFragment } from '@/graphql/api'
import { parseDateTime } from '@/utils/date'
import { useDateFormat } from '@/composables/useDateFormat'
import { getInterval } from '@/helpers/globals'
import LazyLoad from '@/components/LazyLoad.vue'

const formatDate = useDateFormat({
    month: 'long',
    day: 'numeric',
    year: 'numeric',
})

const { t } = useI18n()

const props = defineProps<{
    data: DeliveryTimeframeFragment
}>()

const date = computed(() => parseDateTime(props.data.date))
const validUntil = computed(() => parseDateTime(props.data.validUntil))

const calculate = (): number =>
    validUntil.value.getTime() - new Date().getTime()

const timeLeft: Ref<number> = ref(calculate())

const interval = getInterval()

if (interval) {
    const { setInterval, clearInterval } = interval

    const intervalId = setInterval(() => {
        timeLeft.value = calculate()
    }, 1000)

    onBeforeUnmount(() => clearInterval(intervalId))
}

const MILLISECONDS_SECOND = 1000
const MILLISECONDS_MINUTE = 60 * MILLISECONDS_SECOND
const MILLISECONDS_HOUR = 60 * MILLISECONDS_MINUTE
const MILLISECONDS_DAY = 24 * MILLISECONDS_HOUR

const timeLeftFormatted = computed(() => ({
    days: Math.floor(timeLeft.value / MILLISECONDS_DAY),
    hours: Math.floor((timeLeft.value % MILLISECONDS_DAY) / MILLISECONDS_HOUR),
    minutes: Math.floor(
        (timeLeft.value % MILLISECONDS_HOUR) / MILLISECONDS_MINUTE
    ),
    seconds: Math.floor(
        (timeLeft.value % MILLISECONDS_MINUTE) / MILLISECONDS_SECOND
    ),
}))
</script>

<style lang="scss" scoped>
.delivery-timeframe {
    border: 1px solid $grey-200;
    padding: 25px 20px 15px;
    margin: 0 0 25px;
    font-size: 0.875rem;

    :deep(p) {
        margin: 0 0 10px;
    }

    :deep(a) {
        text-decoration: underline;
    }

    :deep(.icon) {
        font-weight: 900;
        font-size: 11px;
        margin-left: 5px;
    }
}
</style>
